import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { CalendarBlank, CaretLeft, CaretRight } from '@phosphor-icons/react';
import './DatePicker.css';

const HEBREW_MONTHS = [
  'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
  'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר',
];

const HEBREW_DAYS_SHORT = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'];

function isoToDisplay(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return '';
  return `${d}/${m}/${y}`;
}

function displayToIso(display) {
  const match = display.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;
  const [, d, m, y] = match;
  const day = parseInt(d, 10);
  const month = parseInt(m, 10);
  const year = parseInt(y, 10);
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) return null;
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return `${y}-${m}-${d}`;
}

function DatePicker({
  value = '',
  onChange,
  disabled = false,
  error = false,
  name,
  className = '',
  placeholder = 'DD/MM/YYYY',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(isoToDisplay(value));
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const today = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  const selectedDate = value ? new Date(value + 'T00:00:00') : null;

  const [viewYear, setViewYear] = useState(selectedDate ? selectedDate.getFullYear() : new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(selectedDate ? selectedDate.getMonth() : new Date().getMonth());

  useEffect(() => {
    setInputValue(isoToDisplay(value));
    if (value) {
      const d = new Date(value + 'T00:00:00');
      if (!isNaN(d.getTime())) {
        setViewYear(d.getFullYear());
        setViewMonth(d.getMonth());
      }
    }
  }, [value]);

  const emitChange = useCallback((isoVal) => {
    const syntheticEvent = { target: { name, value: isoVal } };
    onChange?.(syntheticEvent);
  }, [name, onChange]);

  const handleDateSelect = (day) => {
    const iso = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setInputValue(isoToDisplay(iso));
    emitChange(iso);
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    let raw = e.target.value.replace(/[^\d/]/g, '');

    const prevLen = inputValue.length;
    const newLen = raw.length;
    const isAdding = newLen > prevLen;

    if (isAdding && (newLen === 2 || newLen === 5) && !raw.endsWith('/')) {
      raw = raw + '/';
    }

    if (raw.length > 10) raw = raw.slice(0, 10);
    setInputValue(raw);

    const iso = displayToIso(raw);
    if (iso) {
      emitChange(iso);
      const d = new Date(iso + 'T00:00:00');
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
    }
  };

  const handleInputBlur = () => {
    if (!inputValue) return;
    const iso = displayToIso(inputValue);
    if (iso) {
      setInputValue(isoToDisplay(iso));
      emitChange(iso);
    } else {
      setInputValue(isoToDisplay(value));
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleInputBlur();
      inputRef.current?.blur();
    }
    if (e.key === 'Escape') {
      setInputValue(isoToDisplay(value));
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (
        containerRef.current && !containerRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = 320;
    const top = spaceBelow < dropdownHeight
      ? rect.top - dropdownHeight - 4
      : rect.bottom + 4;
    setDropdownPos({ top, left: rect.left });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const onScrollOrResize = () => updateDropdownPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isOpen, updateDropdownPosition]);

  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen((prev) => {
      if (!prev) updateDropdownPosition();
      return !prev;
    });
  };

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const calendarDays = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();

    const cells = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      cells.push({ day: daysInPrevMonth - i, current: false });
    }

    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ day: d, current: true });
    }

    const remaining = 7 - (cells.length % 7);
    if (remaining < 7) {
      for (let d = 1; d <= remaining; d++) {
        cells.push({ day: d, current: false });
      }
    }

    return cells;
  }, [viewYear, viewMonth]);

  const isSelected = (day) => {
    if (!selectedDate) return false;
    return (
      selectedDate.getFullYear() === viewYear &&
      selectedDate.getMonth() === viewMonth &&
      selectedDate.getDate() === day
    );
  };

  const isToday = (day) => {
    const [ty, tm, td] = today.split('-').map(Number);
    return viewYear === ty && viewMonth === tm - 1 && day === td;
  };

  return (
    <div
      className={`ck-datepicker ${isOpen ? 'ck-datepicker--open' : ''} ${error ? 'ck-datepicker--error' : ''} ${disabled ? 'ck-datepicker--disabled' : ''} ${className}`}
      ref={containerRef}
    >
      <div className="ck-datepicker__input-row">
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          className="ck-datepicker__input"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          dir="ltr"
        />
        <button
          type="button"
          className="ck-datepicker__toggle"
          onClick={toggleDropdown}
          disabled={disabled}
          tabIndex={-1}
          aria-label="בחירת תאריך"
          aria-expanded={isOpen}
        >
          <CalendarBlank size={16} weight={isOpen ? 'fill' : 'regular'} />
        </button>
      </div>

      {isOpen && createPortal(
        <>
          <div className="ck-datepicker__backdrop" onClick={() => setIsOpen(false)} />
          <div
            ref={dropdownRef}
            className="ck-datepicker__dropdown ck-datepicker__dropdown--portal"
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
          >
            <div className="ck-datepicker__mobile-header">
              <span>בחירת תאריך</span>
              <button type="button" className="ck-datepicker__done" onClick={() => setIsOpen(false)}>
                סיום
              </button>
            </div>

            {/* RTL: first button renders on the RIGHT — right arrow goes to the
                PREVIOUS month (same convention as fin-month-nav). */}
            <div className="ck-datepicker__nav">
              <button type="button" className="ck-datepicker__nav-btn" onClick={prevMonth} aria-label="חודש קודם">
                <CaretRight size={16} weight="bold" />
              </button>
              <span className="ck-datepicker__nav-title">
                {HEBREW_MONTHS[viewMonth]} {viewYear}
              </span>
              <button type="button" className="ck-datepicker__nav-btn" onClick={nextMonth} aria-label="חודש הבא">
                <CaretLeft size={16} weight="bold" />
              </button>
            </div>

            <div className="ck-datepicker__grid">
              <div className="ck-datepicker__weekdays">
                {HEBREW_DAYS_SHORT.map((d) => (
                  <div key={d} className="ck-datepicker__weekday">{d}</div>
                ))}
              </div>
              <div className="ck-datepicker__days">
                {calendarDays.map((cell, i) => (
                  <button
                    key={i}
                    type="button"
                    className={[
                      'ck-datepicker__day',
                      !cell.current && 'ck-datepicker__day--other',
                      cell.current && isSelected(cell.day) && 'ck-datepicker__day--selected',
                      cell.current && isToday(cell.day) && 'ck-datepicker__day--today',
                    ].filter(Boolean).join(' ')}
                    onClick={() => cell.current && handleDateSelect(cell.day)}
                    disabled={!cell.current}
                    tabIndex={cell.current ? 0 : -1}
                  >
                    {cell.day}
                  </button>
                ))}
              </div>
            </div>

            <div className="ck-datepicker__footer">
              <button
                type="button"
                className="ck-datepicker__today-btn"
                onClick={() => {
                  const [y, m] = today.split('-').map(Number);
                  setViewYear(y);
                  setViewMonth(m - 1);
                  setInputValue(isoToDisplay(today));
                  emitChange(today);
                  setIsOpen(false);
                }}
              >
                היום
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

export default DatePicker;
